'use client';

import dynamic from 'next/dynamic';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Fragment, startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams, useServerInsertedHTML } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Spinner } from '@zillow/constellation-10';

import { css, cx } from '@/styled-system/css';
import { usePathname } from '@/src/i18n/navigation';
import { emitTrackToggleViewModeEvent } from '@/src/analytics/hotpads/events/srp';
import {
  contentPanel,
  contentPanelChildren,
  contentPanelDesktopBottomInset,
  contentPanelMobileMapActive,
  mapFallbackContainer,
  mapPanel,
  mapPanelMobileActive,
  mobileMapSearchBarOverlay,
  searchLayout,
} from '@/src/layouts/persistentSearchLayout.styles';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import type { StaticMapBounds } from '@/src/lib/utils/staticMap';
import { parseMapPositionFromSearchParams } from '@/src/lib/map/viewportBounds';
import type { MapViewportSize } from '@/src/lib/map/viewportBounds';
import { SRP_LISTINGS_VIEW } from '../_srp/_data/srpListingsView';
import { buildStaticMapPreviewHeadBootstrapScript, StaticMapPreview } from './StaticMapPreview';
import { useDeferredMapIsland } from './useDeferredMapIsland';
import { useSetActiveListing, useSetMapHoveredMarker, useSetMapPinnedMarker } from '@/src/lib/hooks';
import {
  consumeSearchHdpNavigationScrollReset,
  restoreQueuedSearchHdpScroll,
} from '@/src/lib/navigation/searchHdpHistory';
import {
  clearPendingMobileFeedRestoreScrollY,
  setPendingMobileFeedRestoreScrollY,
} from '@/src/lib/navigation/mobileFeedTransitionScroll';
import {
  popMobileMapHistoryEntry,
  pushMobileMapHistoryEntry,
  readMobileMapHistoryOpen,
  writeMobileMapHistoryOpen,
} from '@/src/lib/navigation/mobileMapHistory';
import { MobileSrpBottomControls } from './MobileSrpBottomControls';
import { useSrpViewMode, type ViewMode } from './SearchViewModeContext';

type Props = {
  children: ReactNode;
  stickySearchBar?: ReactNode;
  mobileMapSearchBar?: ReactNode;
  mapPreviewBounds?: StaticMapBounds;
};

// Desktop-only chrome (display none below lg keeps it off the mobile map's
// touch path); while opaque it intercepts pointer events over the live map.
const mapPreviewOverlay = css({
  display: { base: 'none', lg: 'block' },
  position: 'absolute',
  inset: 0,
  zIndex: 'zIndexOverlay',
  opacity: 1,
  transitionProperty: 'opacity',
  // Matches MAP_PREVIEW_FADE_OUT_MS in useDeferredMapIsland.
  transitionDuration: '400ms',
  transitionTimingFunction: 'ease',
  '&[data-map-ready="true"]': {
    opacity: 0,
    pointerEvents: 'none',
  },
});

// A spinner rather than placeholder text: below lg the static map preview is display:none, so
// this fallback is all the renter sees while the island loads.
function MapIslandFallback() {
  const t = useTranslations('search');
  return (
    <div className={mapFallbackContainer} role="status">
      <Spinner title={t('loadingMap')} />
    </div>
  );
}

const SearchMapIsland = dynamic(() => import('./SearchMapIsland').then((m) => m.SearchMapIsland), {
  ssr: false,
  loading: () => <MapIslandFallback />,
});

function isHdpPath(pathname: string) {
  const finalSegment = pathname.split('/').filter(Boolean).at(-1);
  return finalSegment === 'pad' || finalSegment === 'building';
}

// The mobile map has no URL param, so a load always starts on the list; each history entry records
// whether the map was open on it, so Back and Forward still close and reopen it.
function useMobileMapHistory(viewMode: ViewMode, setViewMode: Dispatch<SetStateAction<ViewMode>>, locationKey: string) {
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const open = readMobileMapHistoryOpen(event.state);
      startTransition(() => setViewMode((current) => (open ? 'map' : current === 'map' ? 'grid' : current)));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setViewMode]);

  const isMapOpen = viewMode === 'map';
  useEffect(() => {
    // window.location rather than the rendered route: after a popstate it is already the destination.
    if (isMapOpen && isHdpPath(window.location.pathname)) {
      setViewMode('grid');
      return;
    }
    writeMobileMapHistoryOpen(isMapOpen);
  }, [isMapOpen, locationKey, setViewMode]);
}

function getWindowScrollY(): number {
  return window.scrollY || window.pageYOffset || 0;
}

export function PersistentSearchShell({ children, stickySearchBar, mobileMapSearchBar, mapPreviewBounds }: Props) {
  const isDesktop = useIsDesktop();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { viewMode, setViewMode } = useSrpViewMode();
  const hdpRoute = isHdpPath(pathname);
  const mobileMapScrollYRef = useRef<number | null>(null);
  const pendingMobileMapScrollRestoreRef = useRef<number | null>(null);
  const pendingMobileFeedFrameRef = useRef(0);
  const pendingMobileFeedTokenRef = useRef(0);
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const wasHdpRouteRef = useRef(hdpRoute);
  const [mapViewportSize, setMapViewportSize] = useState<MapViewportSize>();
  const setMapPinnedMarker = useSetMapPinnedMarker();
  const setMapHoveredMarker = useSetMapHoveredMarker();
  const setActiveListing = useSetActiveListing();
  const effectiveViewMode: ViewMode = isDesktop ? 'grid' : viewMode;
  const mobileMapActive = !hdpRoute && effectiveViewMode === 'map';
  useMobileMapHistory(viewMode, setViewMode, `${pathname}?${searchParams.toString()}`);
  const shouldRenderMap = isDesktop || mobileMapActive;
  // Skip the preview when the URL seeds a viewport, per the island's own parse.
  const [hasExplicitViewportUrl] = useState(
    () => parseMapPositionFromSearchParams(searchParams, SRP_LISTINGS_VIEW) !== null,
  );
  const shouldBootstrapStaticMap = Boolean(mapPreviewBounds) && !hasExplicitViewportUrl;
  const insertedStaticMapBootstrapRef = useRef(false);
  useServerInsertedHTML(() => {
    if (insertedStaticMapBootstrapRef.current || !shouldBootstrapStaticMap || !mapPreviewBounds) return null;
    insertedStaticMapBootstrapRef.current = true;
    return (
      <script
        data-static-map-preview-head-bootstrap
        dangerouslySetInnerHTML={{ __html: buildStaticMapPreviewHeadBootstrapScript(mapPreviewBounds) }}
      />
    );
  });
  const { showIsland, showPreview, isMapReady, onActivate, onMapReady, onPreviewLoad, onPreviewError } =
    useDeferredMapIsland({
      hasPreview: shouldBootstrapStaticMap,
      shouldRenderMap,
      mobileMapActive,
      pathname,
    });
  const shouldShowMobileControls = !isDesktop && !hdpRoute;

  useLayoutEffect(() => {
    const panel = mapPanelRef.current;
    if (!panel) return;

    const measure = () => {
      const { width, height } = panel.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      setMapViewportSize((current) =>
        current?.width === width && current.height === height ? current : { width, height },
      );
    };

    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(panel);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useLayoutEffect(() => {
    if (hdpRoute) return;
    restoreQueuedSearchHdpScroll(window.location.href);
  }, [hdpRoute, pathname]);

  useLayoutEffect(() => {
    if (!hdpRoute || !consumeSearchHdpNavigationScrollReset(window.location.href)) return;
    window.scrollTo(0, 0);
  }, [hdpRoute, pathname]);

  useLayoutEffect(() => {
    if (isDesktop && viewMode !== 'grid') {
      setViewMode('grid');
    }
  }, [isDesktop, setViewMode, viewMode]);

  useEffect(() => {
    if (wasHdpRouteRef.current && !hdpRoute) {
      setMapPinnedMarker(null);
      setMapHoveredMarker(null);
      setActiveListing(null);
    }

    wasHdpRouteRef.current = hdpRoute;
  }, [hdpRoute, setActiveListing, setMapHoveredMarker, setMapPinnedMarker]);

  useLayoutEffect(() => {
    if (mobileMapActive) return;
    const scrollY = pendingMobileMapScrollRestoreRef.current;
    if (scrollY == null) return;

    pendingMobileMapScrollRestoreRef.current = null;
    window.scrollTo(0, scrollY);
    window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }, [mobileMapActive]);

  useEffect(() => {
    return () => {
      pendingMobileFeedTokenRef.current += 1;
      if (pendingMobileFeedFrameRef.current) {
        window.cancelAnimationFrame(pendingMobileFeedFrameRef.current);
        pendingMobileFeedFrameRef.current = 0;
      }
    };
  }, []);

  const cancelPendingMobileFeedTransition = useCallback(() => {
    pendingMobileFeedTokenRef.current += 1;
    clearPendingMobileFeedRestoreScrollY();
    if (pendingMobileFeedFrameRef.current) {
      window.cancelAnimationFrame(pendingMobileFeedFrameRef.current);
      pendingMobileFeedFrameRef.current = 0;
    }
  }, []);

  // A transition lets the tap paint before React relayouts the long listing document (HP-8727).
  const switchViewMode = useCallback(
    (nextViewMode: ViewMode) => {
      startTransition(() => setViewMode(nextViewMode));
    },
    [setViewMode],
  );

  const openMobileMap = useCallback(() => {
    mobileMapScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    pushMobileMapHistoryEntry();

    setMapPinnedMarker(null);
    setActiveListing(null);
    switchViewMode('map');
  }, [setActiveListing, setMapPinnedMarker, switchViewMode]);

  const closeMobileMap = useCallback(
    (nextViewMode: Exclude<ViewMode, 'map'>) => {
      pendingMobileMapScrollRestoreRef.current = mobileMapScrollYRef.current;
      setMapPinnedMarker(null);
      setActiveListing(null);
      switchViewMode(nextViewMode);
      popMobileMapHistoryEntry();
    },
    [setActiveListing, setMapPinnedMarker, switchViewMode],
  );

  const switchToFeedAfterWindowTop = useCallback(() => {
    const restoreScrollY = getWindowScrollY();
    if (restoreScrollY <= 1) {
      clearPendingMobileFeedRestoreScrollY();
      switchViewMode('feed');
      return;
    }

    setPendingMobileFeedRestoreScrollY(restoreScrollY);
    const transitionToken = pendingMobileFeedTokenRef.current;
    let attempts = 0;

    const tick = () => {
      if (transitionToken !== pendingMobileFeedTokenRef.current) return;

      window.scrollTo(0, 0);
      attempts += 1;

      if (getWindowScrollY() <= 1 || attempts >= 12) {
        pendingMobileFeedFrameRef.current = 0;
        switchViewMode('feed');
        return;
      }

      pendingMobileFeedFrameRef.current = window.requestAnimationFrame(tick);
    };

    tick();
  }, [switchViewMode]);

  const handleMobileControlChange = useCallback(
    (nextValue: ViewMode) => {
      cancelPendingMobileFeedTransition();

      if (nextValue === effectiveViewMode) return;

      emitTrackToggleViewModeEvent({ targetView: nextValue === 'grid' ? 'list' : nextValue });

      if (nextValue === 'map') {
        openMobileMap();
        return;
      }

      if (mobileMapActive) {
        closeMobileMap(nextValue);
      } else if (nextValue === 'feed') {
        switchToFeedAfterWindowTop();
      } else {
        switchViewMode(nextValue);
      }
    },
    [
      cancelPendingMobileFeedTransition,
      closeMobileMap,
      effectiveViewMode,
      mobileMapActive,
      openMobileMap,
      switchToFeedAfterWindowTop,
      switchViewMode,
    ],
  );

  return (
    <div
      id="srp-shell"
      data-srp-component="shell"
      data-srp-view-mode={effectiveViewMode}
      data-mobile-map-active={mobileMapActive ? 'true' : 'false'}
      className={searchLayout}
    >
      <div
        id="srp-map-panel"
        ref={mapPanelRef}
        data-srp-component="map-panel"
        className={mobileMapActive ? mapPanelMobileActive : mapPanel}
        onPointerEnter={onActivate}
        onPointerDown={onActivate}
        onFocus={onActivate}
      >
        {showIsland ? (
          <SearchMapIsland isMobileMapActive={mobileMapActive} viewportSize={mapViewportSize} onMapReady={onMapReady} />
        ) : null}
        {showPreview && mapPreviewBounds ? (
          <div className={mapPreviewOverlay} data-map-ready={isMapReady ? 'true' : 'false'} aria-hidden="true">
            <StaticMapPreview bounds={mapPreviewBounds} onLoad={onPreviewLoad} onError={onPreviewError} />
          </div>
        ) : null}
      </div>

      <div
        id="srp-content-panel"
        className={cx(
          contentPanel,
          contentPanelDesktopBottomInset,
          mobileMapActive ? contentPanelMobileMapActive : undefined,
        )}
        data-search-content-panel
        data-srp-component="content-panel"
        data-srp-view-mode={effectiveViewMode}
        data-mobile-map-active={mobileMapActive ? 'true' : 'false'}
      >
        {mobileMapActive && mobileMapSearchBar ? (
          <div className={mobileMapSearchBarOverlay} data-srp-component="mobile-map-search-bar">
            {mobileMapSearchBar}
          </div>
        ) : null}
        <div className={contentPanelChildren} hidden={mobileMapActive}>
          {/* The sticky SRP search bar lives here (not in the page) so it survives the
              slug morph that remounts the page; it's hidden with its content children in
              mobile-map mode (the floating overlay above replaces it) and on HDP routes. */}
          {hdpRoute || !stickySearchBar ? null : <Fragment key="sticky-search-bar">{stickySearchBar}</Fragment>}
          {children}
        </div>
      </div>
      {shouldShowMobileControls ? (
        <MobileSrpBottomControls value={effectiveViewMode} onValueChange={handleMobileControlChange} />
      ) : null}
    </div>
  );
}
